import { useState } from "react";
import type { ReviewDTO } from "../../shared/api";
import { Stars } from "./Stars";
import { VerificationBadge } from "./Badge";
import { api } from "../lib/api";
import { TimeAgo } from "../lib/format";
import { dimensionsFor, type EntityType } from "../../shared/dimensions";
import { personName } from "../../shared/people";

const labelFor = (type: EntityType, key: string) =>
  dimensionsFor(type).find((d) => d.key === key)?.label ?? key;

export function ReviewCard({ review, entityType }: { review: ReviewDTO; entityType: EntityType }) {
  const [helpful, setHelpful] = useState(review.helpfulCount);
  const [voted, setVoted] = useState(false);
  const [flagged, setFlagged] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (review.status === "removed") {
    return (
      <div className="review removed" role="note">
        This review was removed for a Terms of Service violation. The score impact has been excluded.
      </div>
    );
  }

  const vote = async () => {
    setErr(null);
    try {
      await api.voteReview(review.id, "helpful");
      setHelpful((h) => h + 1);
      setVoted(true);
    } catch (e) {
      setErr((e as Error).message);
    }
  };
  const flag = async () => {
    const reason = prompt("Why are you reporting this review?");
    if (!reason) return;
    try {
      await api.flagReview(review.id, reason);
      setFlagged(true);
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  return (
    <article className={`review ${review.verificationLevel === "none" ? "unverified" : ""}`}>
      <div className="rhead">
        <div className="row gap-8">
          <Stars value={review.ratingOverall} />
          <span className="author">{personName(review.author)}</span>
        </div>
        <VerificationBadge level={review.verificationLevel} />
      </div>

      {review.title && <h3 className="u-0cc3319">{review.title}</h3>}
      {review.body && <p className="u-725930c">{review.body}</p>}

      {review.dimensions.length > 0 && (
        <div className="dims">
          {review.dimensions.map((d) => (
            <span className="dim" key={d.key}>
              <span className="muted">{labelFor(entityType, d.key)}</span>
              <Stars value={d.score} />
            </span>
          ))}
        </div>
      )}

      {review.response && (
        <div className="response">
          <strong className="u-481a3cd">Response from the team</strong>
          <p className="u-4f874b8">{review.response.body}</p>
        </div>
      )}

      <div className="ractions">
        <button className="btn btn-ghost btn-sm" onClick={vote} disabled={voted}>
          👍 Helpful <span className="tnum">{helpful}</span>
        </button>
        <button className="btn btn-ghost btn-sm" onClick={flag} disabled={flagged}>
          {flagged ? "Reported" : "Report"}
        </button>
        <span className="muted u-f56c3a8"><TimeAgo iso={review.createdAt} /></span>
      </div>
      {err && <p className="u-3f02914">{err}</p>}
    </article>
  );
}
